import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  Search, MapPin, Utensils, Bed, Car, Wrench, Home, Heart,
  Briefcase, GraduationCap, Stethoscope, Anchor, Plane, 
  ShoppingBag, Dumbbell, Sparkles, Theater, Building, Laptop,
  PawPrint, Flower, PartyPopper, ChevronRight, ArrowLeft,
  Star, Phone, Globe, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  AccommodationBookingWidget, 
  CarHireBookingWidget, 
  ToursActivitiesWidget,
  AffiliateBookingSection 
} from '../../components/AffiliateWidgets';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for categories
const categoryIcons = {
  'accommodation': Bed,
  'food-dining': Utensils,
  'arts-entertainment': Theater,
  'sporting-leisure': Dumbbell,
  'health-beauty': Sparkles,
  'automotive': Car,
  'shopping': ShoppingBag,
  'services': Wrench,
  'tradesman': Wrench,
  'business-finance': Briefcase,
  'builders': Building,
  'boats-boating': Anchor,
  'weddings': Heart,
  'party-hire': PartyPopper,
  'car-hire': Car,
  'it-web': Laptop,
  'office-services': Building,
  'real-estate': Home,
  'education': GraduationCap,
  'pets': PawPrint,
  'home-garden': Flower,
  'transport-travel': Plane,
  'tourism': MapPin,
  'medical': Stethoscope,
};

const CityPage = () => {
  const { cityId } = useParams();
  const [location, setLocation] = useState(null);
  const [categories, setCategories] = useState([]);
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (cityId) {
      fetchLocationData();
    }
  }, [cityId]);

  const fetchLocationData = async () => {
    try {
      setLoading(true);
      
      // Fetch location details with category counts
      const locationRes = await fetch(`${API_URL}/api/hotspots/locations/${cityId}`);
      if (!locationRes.ok) throw new Error('Location not found');
      const locationData = await locationRes.json();
      setLocation(locationData);
      
      // Get categories from config
      const configRes = await fetch(`${API_URL}/api/hotspots/config`);
      const configData = await configRes.json();
      setCategories(configData.categories || []);
      
      // Fetch featured businesses for this location
      const businessRes = await fetch(`${API_URL}/api/hotspots/businesses?location_id=${cityId}&featured=true&limit=6`);
      const businessData = await businessRes.json();
      setFeaturedBusinesses(businessData.businesses || []);
      
    } catch (error) {
      console.error('Error fetching location data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/hotspots/${cityId}/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 flex items-center justify-center">
        <div className="animate-pulse text-sky-600 text-xl">Loading {cityId?.replace('-', ' ')}...</div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Location Not Found</h1>
          <Link to="/hotspots" className="text-sky-600 hover:underline">Back to Australian Hotspots</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-sky-600 to-sky-800 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Breadcrumb */}
          <Link 
            to="/hotspots"
            className="inline-flex items-center gap-2 text-sky-200 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Australian Hotspots
          </Link>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-sky-200 mb-2">
                <MapPin className="h-4 w-4" />
                <span>{location.state_full}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3">{location.name}</h1>
              <p className="text-xl text-sky-100">{location.tagline}</p>
              <p className="text-sky-200 mt-2 max-w-2xl">{location.description}</p>
            </div>

            {/* Search in this city */}
            <form onSubmit={handleSearch} className="w-full md:w-auto">
              <div className="flex gap-2 p-2 bg-white/10 backdrop-blur-sm rounded-xl">
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
                  <Input
                    type="text"
                    placeholder={`Search in ${location.name}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-transparent border-white/20 text-white placeholder:text-white/60 focus-visible:ring-white/50"
                    data-testid="city-search-input"
                  />
                </div>
                <Button 
                  type="submit"
                  className="bg-white text-sky-600 hover:bg-sky-50"
                  data-testid="city-search-btn"
                >
                  Search
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Sub-locations Pills */}
      {location.sub_locations && location.sub_locations.length > 0 && (
        <section className="bg-white border-b border-slate-200 py-4 overflow-x-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 flex-nowrap">
              <span className="text-sm text-slate-500 whitespace-nowrap">Popular areas:</span>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {location.sub_locations.map((sub) => (
                  <Link
                    key={sub.slug}
                    to={`/hotspots/${cityId}/${sub.slug}`}
                    className="px-4 py-2 bg-slate-100 hover:bg-sky-100 text-slate-700 hover:text-sky-700 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
                    data-testid={`sublocation-pill-${sub.slug}`}
                  >
                    {sub.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* AFFILIATE BOOKING SECTION - Plan Your Trip */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              Plan Your {location.name} Trip
            </h2>
            <p className="text-slate-600">
              Book accommodation, hire a car, and discover amazing experiences
            </p>
          </div>
          
          {/* Full-width Accommodation Widget */}
          <div className="mb-6">
            <AccommodationBookingWidget cityId={cityId} cityName={location.name} variant="full" />
          </div>
          
          {/* Car Hire and Tours side by side */}
          <div className="grid md:grid-cols-2 gap-6">
            <CarHireBookingWidget cityId={cityId} cityName={location.name} variant="full" />
            <ToursActivitiesWidget cityId={cityId} cityName={location.name} variant="full" />
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
            Explore {location.name} by Category
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {categories.map((category) => {
              const IconComponent = categoryIcons[category.id] || MapPin;
              const countInfo = location.category_counts?.find(c => c.category.id === category.id);
              const count = countInfo?.count || 0;
              
              return (
                <Link
                  key={category.id}
                  to={`/hotspots/${cityId}/${category.slug}`}
                  className="group flex flex-col items-center p-5 bg-white rounded-xl border border-slate-200 hover:border-sky-300 hover:shadow-lg transition-all duration-300"
                  data-testid={`city-category-${category.id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-sky-100 group-hover:bg-sky-500 flex items-center justify-center mb-3 transition-colors">
                    <IconComponent className="h-5 w-5 text-sky-600 group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-sm font-medium text-slate-700 text-center leading-tight mb-1">
                    {category.name}
                  </span>
                  {count > 0 && (
                    <span className="text-xs text-slate-400">{count} listings</span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Businesses */}
      {featuredBusinesses.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                Featured in {location.name}
              </h2>
              <Link 
                to={`/hotspots/${cityId}/all`}
                className="text-sky-600 hover:text-sky-700 font-medium flex items-center gap-1"
              >
                View all <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredBusinesses.map((business) => (
                <Link
                  key={business.id}
                  to={`/hotspots/business/${business.id}`}
                  className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300"
                  data-testid={`featured-business-${business.id}`}
                >
                  <div className="aspect-video bg-gradient-to-br from-slate-200 to-slate-300 relative">
                    {business.images?.[0] ? (
                      <img 
                        src={business.images[0]} 
                        alt={business.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Building className="h-12 w-12 text-slate-400" />
                      </div>
                    )}
                    {business.featured && (
                      <div className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                        <Star className="h-3 w-3" />
                        Featured
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg text-slate-900 group-hover:text-sky-600 transition-colors mb-1">
                      {business.name}
                    </h3>
                    <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                      {business.description || 'Quality local business'}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      {business.rating > 0 && (
                        <span className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                          {business.rating.toFixed(1)}
                        </span>
                      )}
                      {business.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-4 w-4" />
                          Contact
                        </span>
                      )}
                      {business.website && (
                        <span className="flex items-center gap-1">
                          <Globe className="h-4 w-4" />
                          Website
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quick Info / CTA */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Own a Business in {location.name}?
          </h2>
          <p className="text-lg text-orange-100 mb-8">
            Get discovered by thousands of visitors. List your business today.
          </p>
          <Button
            size="lg"
            className="bg-white text-orange-600 hover:bg-orange-50"
          >
            Add Your Business - It's Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Lionheart Digital Branding - POWERFUL */}
          <div className="flex flex-col items-center text-center mb-10">
            {/* Big Lion */}
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-red-500/20 blur-3xl rounded-full"></div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/28a7e231-ff1b-4b8f-915f-47798802b078/images/2fa9ddf7953c616448f48ed011e225e9e3469523a5a5c8f53c562308f1d829cc.png" 
                alt="Lionheart Digital" 
                className="h-28 md:h-36 w-auto relative z-10 drop-shadow-2xl"
              />
            </div>
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-black tracking-tight mb-3">
              <span className="text-white">LIONHEART</span>
              <span className="text-orange-500"> DIGITAL</span>
            </h3>
            <p className="text-lg md:text-xl font-bold italic text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-orange-400">
              "They lit the fire. We became it."
            </p>
          </div>
          <div className="border-t border-slate-800 pt-6 text-center">
            <Link to="/hotspots" className="text-xl font-bold">
              <span className="text-sky-400">Australian</span>
              <span className="text-orange-400"> Hotspots</span>
            </Link>
            <p className="text-slate-400 text-sm mt-4">
              &copy; {new Date().getFullYear()} Australian Hotspots. A Lionheart Digital Asset.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CityPage;
